import { useInfiniteQuery } from '@tanstack/react-query'

import { marketShopAPI } from '@/api/new-api'
import { MarketShopClientOrderModel, MarketShopOrderStatus } from '@/models'

export interface GetClientOrdersRequest {
  limit: number
  status?: Array<MarketShopOrderStatus>
}

export type GetClientOrdersResponse = {
  total: number
  data: Array<MarketShopClientOrderModel>
}

export const getClientOrdersQueryKey = (data: GetClientOrdersRequest) => [
  'useGetClientOrders',
  data,
]

const REFETCH_INTERVAL = 30000

export const getClientOrders = async (
  data: GetClientOrdersRequest & { skip: number },
  signal?: AbortSignal,
) => {
  const response = await marketShopAPI.get<GetClientOrdersResponse>('/market-shop/client/orders', {
    params: data,
    signal,
  })
  return response.data
}

export interface UseGetClientOrdersProps {
  data: GetClientOrdersRequest
  enabled?: boolean
}

export const useGetClientOrders = ({ data, enabled }: UseGetClientOrdersProps) => {
  return useInfiniteQuery({
    enabled,
    getNextPageParam: (
      lastPage: GetClientOrdersResponse,
      __allPages: GetClientOrdersResponse[],
      lastPageParam,
      __allPageParams,
    ) => {
      const newPageParam = lastPageParam + data.limit
      if (newPageParam >= lastPage?.total) return undefined
      return newPageParam
    },
    initialPageParam: 0,
    queryFn: ({ pageParam, signal }) => {
      return getClientOrders({ ...data, skip: pageParam }, signal)
    },
    queryKey: getClientOrdersQueryKey(data),
    refetchInterval: REFETCH_INTERVAL,
    // NOTE: we always refetch on mount, so when a user orders something, it gets refetched,
    // since market-shop will be remounted
    refetchOnMount: 'always',
  })
}
